import { UseFormReturn, useFieldArray } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";

interface AcademicBackgroundStepProps {
  form: UseFormReturn<any>;
  onNext: () => void;
  onBack: () => void;
}

export const AcademicBackgroundStep = ({ form, onNext, onBack }: AcademicBackgroundStepProps) => {
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "collegeHistory",
  });

  const highestEducation = form.watch("highestEducation");
  const showCollegeFields = ["some-college", "associate", "bachelor", "master", "doctorate"].includes(highestEducation);

  return (
    <Form {...form}>
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Academic Background</h3>
      
      <FormField
        control={form.control}
        name="highestEducation"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Highest Level of Education Completed *</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select education level" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="high-school">High School</SelectItem>
                <SelectItem value="some-college">Some College</SelectItem>
                <SelectItem value="associate">Associate Degree</SelectItem>
                <SelectItem value="bachelor">Bachelor's Degree</SelectItem>
                <SelectItem value="master">Master's Degree</SelectItem>
                <SelectItem value="doctorate">Doctorate/PhD</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      {showCollegeFields && (
        <div className="space-y-4 p-4 border rounded-lg">
          <div className="flex justify-between items-center">
            <h4 className="font-medium">College/University History</h4>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => append({ institution: "", degree: "", graduationYear: "", major: "" })}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Institution
            </Button>
          </div>

          {fields.map((field, index) => (
            <div key={field.id} className="space-y-4 p-4 border rounded-lg relative">
              {fields.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => remove(index)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}

              <FormField
                control={form.control}
                name={`collegeHistory.${index}.institution`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Institution Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name={`collegeHistory.${index}.degree`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Degree Earned</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., B.A., B.S., M.Div." />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`collegeHistory.${index}.graduationYear`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Graduation Year</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="YYYY" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name={`collegeHistory.${index}.major`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Major/Field of Study</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          ))}

          {fields.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No institutions added yet. Click "Add Institution" to begin.
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="highSchoolName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>High School Name *</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="highSchoolGraduationYear"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Graduation Year *</FormLabel>
              <FormControl>
                <Input {...field} placeholder="YYYY" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={onBack}>Back</Button>
          <Button onClick={onNext}>Next</Button>
        </div>
      </div>
    </Form>
  );
};
